import { Chip, Container, Stack } from "@mui/material";
import LazyTable, {
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import OpenLinkInNewTabContextMenuItem from "components/OpenInNewTabContextMenuItem";
import SearchBoxPersisted from "components/SearchBoxPersisted";
import useGlobalContext from "hooks/globalData";
import useListTitle from "hooks/useListTitle";
import { FC, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import {
  ILicenseListItem,
  LicenseAdminFilter,
  ListRequestOfLicenseAdminFilter,
} from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import persistanceStore from "stores/persistanceStore";
import LicenseFilter from "./LicenseFilter";

const withAcronym = (name?: string, acronym?: string) =>
  acronym ? `${name} (${acronym})` : name ?? "-";

const accreditationBodyDisplay = (name?: string, acronym?: string) => {
  if (!name?.trim()) return "-";
  return acronym?.trim() ? `${name} / ${acronym}` : name;
};

const ProfessionsCell: FC<{ value: Pick<ILicenseListItem, "professions"> }> = ({
  value,
}) => {
  const professions: string[] = value?.professions ?? [];
  if (professions.length === 0) return <>-</>;
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {professions.map((profession, index) => (
        <Chip key={index} label={profession} size="small" />
      ))}
    </Stack>
  );
};

const LicenseList = () => {
  const { formatMessage: t } = useIntl();
  const navigate = useNavigate();
  const { licenseService, licenseDetailsUrl } = useGlobalContext();
  const [order, setOrder] = useState<TableSortType>("asc");
  const [orderBy, setOrderBy] = useState("Location.Name");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const { organizationId } = contextStore.useState();
  const {
    licenseSearchTerm,
    licenseFilterDesignationIds,
    licenseFilterProfessionIds,
    licenseFilterLocationIds,
    licenseFilterLicensureBodyIds,
  } = persistanceStore.useState();

  useListTitle("licensesLabel");

  // Reset to the first page when the organization context changes.
  useEffect(() => {
    setPage(0);
  }, [organizationId]);

  const listRequest = ListRequestOfLicenseAdminFilter.fromJS({
    pageNumber: page,
    pageSize: rowsPerPage,
    sortField: orderBy,
    sortDescending: order === "desc",
    parameters: LicenseAdminFilter.fromJS({
      searchText: licenseSearchTerm || undefined,
      designationIds: licenseFilterDesignationIds,
      professionIds: licenseFilterProfessionIds,
      locationIds: licenseFilterLocationIds,
      licensureBodyIds: licenseFilterLicensureBodyIds,
    }),
  });

  const { data, isFetching } = useQuery(
    ["licenseService.getLicenses", organizationId, listRequest],
    () => licenseService.getLicenses(listRequest),
    {
      keepPreviousData: true,
    },
  );

  const licenses = ((data?.data as ILicenseListItem[]) ?? []).map((l) => ({
    ...l,
    id: l.id!,
    designationDisplay: withAcronym(l.designationName, l.designationAcronym),
    accreditationBodyDisplay: accreditationBodyDisplay(
      l.accreditationBodyName,
      l.accreditationBodyAcronym,
    ),
    submissionAuthorityDisplay: l.submissionAuthorityName?.trim()
      ? l.submissionAuthorityName
      : "-",
  }));

  const cells: Array<TableCellType> = [
    {
      accessor: "designationDisplay",
      customSortAccessor: "Designation.Name",
      type: "text",
      label: t({ id: "designation" }),
    },
    {
      accessor: "locationName",
      customSortAccessor: "Location.Name",
      type: "text",
      label: t({ id: "locationLabel" }),
    },
    {
      accessor: "licensureBodyName",
      type: "text",
      noSort: true,
      label: t({ id: "licensureBody" }),
    },
    {
      accessor: "accreditationBodyDisplay",
      type: "text",
      noSort: true,
      label: t({ id: "accreditationBody" }),
    },
    {
      accessor: "submissionAuthorityDisplay",
      type: "text",
      noSort: true,
      label: t({ id: "submissionAuthority" }),
    },
    {
      accessor: "professions",
      type: "node",
      noSort: true,
      label: t({ id: "professions" }),
      View: ProfessionsCell,
    },
  ];

  const onClick = (id: string) => {
    navigate(licenseDetailsUrl(id));
  };

  const handleSetPersistedSearchTerm = (searchText: string): void => {
    persistanceStore.update((s) => {
      s.licenseSearchTerm = searchText;
    });
  };

  return (
    <Container>
      <SearchBoxPersisted
        setPersistedSearchTerm={handleSetPersistedSearchTerm}
        persistedSearchTerm={licenseSearchTerm}
      />

      <LicenseFilter />

      <LazyTable
        {...{
          rowCount: data?.totalRecords ?? 0,
          rows: licenses,
          loading: isFetching,
          dense: true,
          title: t({ id: "licensesLabel" }),
          cells,
          page,
          setPage,
          order,
          setOrder,
          orderBy,
          setOrderBy,
          rowsPerPage,
          setRowsPerPage,
          onClick,
          contextMenuItems: [
            OpenLinkInNewTabContextMenuItem((id) => licenseDetailsUrl(id)),
          ],
        }}
      />
    </Container>
  );
};

export default LicenseList;
